import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import NewTextbox from './NewTextbox';
import cryptoService from '../../services/cryptoService';
import keyService from '../../services/keyService';
import { parseEncryptionKey } from '../../utils/decryptionUtils';

const NewTextboxContainer = () => {
  const { state } = useAuth();
  const [textToEncryption, setTextToEncryption] = useState('');
  const [subject, setSubject] = useState('');
  const [encryptedText, setEncryptedText] = useState('');
  const [showKey, setShowKey] = useState(false);
  // v2 key: 64 hex chars, generated once per form; the IV is per-message and
  // lives inside the ciphertext, so reusing this key stays safe.
  const [encryptionKeyObject] = useState(() => cryptoService.generateKeyV2());
  const [messageId, setMessageId] = useState(null);
  const [keyId, setKeyId] = useState(null);
  const createdDate = new Date();

  const handleChange = (event) => {
    setTextToEncryption(event.target.value);
  };

  const handleSubjectChange = (event) => {
    setSubject(event.target.value);
  };

  // Programmatic fill used by the template picker
  const applyTemplate = (templateSubject, templateContent) => {
    setSubject(templateSubject);
    setTextToEncryption(templateContent);
  };

  // Helper function to generate default subject if empty
  const getDefaultSubject = () => {
    const now = new Date();
    const dateStr = now.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    const timeStr = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
    return `message ${dateStr} ${timeStr}`;
  };

  /**
   * Encrypt and (except for passphrase mode) persist the key.
   * @param {Object|null} selectedKey - an existing key object (server or local) or null
   * @param {string|null} passphrase - when set, passphrase mode is used: the key is
   *   derived from the passphrase and nothing is stored anywhere
   */
  const handleGenerateEncryption = async (selectedKey = null, passphrase = null) => {
    try {
      // If subject is empty, generate a default one
      let finalSubject = subject.trim();
      if (!finalSubject) {
        finalSubject = getDefaultSubject();
        setSubject(finalSubject);
      }

      // ── Passphrase mode: nothing is stored, the passphrase is the key ──
      if (passphrase) {
        const encryptedResult = await cryptoService.encryptWithPassphrase({
          text: textToEncryption,
          passphrase,
        });
        setEncryptedText(encryptedResult.encryptedText);
        return {
          message: null,
          key: null,
          isPassphrase: true,
          encryptedText: encryptedResult.encryptedText,
          subject: finalSubject,
        };
      }

      let result = null;
      let encryptedResult = null;

      if (selectedKey) {
        // Reuse an existing key (server or local, v1 or v2). Encryption always
        // produces the v2 format with a fresh IV; v1 keys keep working because
        // their first 64 hex chars are the raw AES key.
        const parsed = parseEncryptionKey(selectedKey.encryption_key);
        if (!parsed) {
          throw new Error('The selected key has an invalid format');
        }

        encryptedResult = await cryptoService.encryptV2({
          text: textToEncryption,
          key: parsed.key,
        });

        setEncryptedText(encryptedResult.encryptedText);

        if (selectedKey.isLocal) {
          result = {
            message: null,
            key: selectedKey,
            isExistingKey: true,
            isLocalKey: true,
            encryptedText: encryptedResult.encryptedText,
            subject: finalSubject
          };
        } else if (state.isLoggedIn) {
          const message = await keyService.createMessage({ subject: finalSubject });
          await keyService.addKeyToMessage(message.id, selectedKey.id);

          setMessageId(message.id);
          setKeyId(selectedKey.id);

          result = {
            message,
            key: selectedKey,
            isExistingKey: true,
            isLocalKey: false,
            encryptedText: encryptedResult.encryptedText,
            subject: finalSubject
          };
        }
      } else {
        // Encrypt with the freshly generated v2 key
        encryptedResult = await cryptoService.encryptV2({
          text: textToEncryption,
          key: encryptionKeyObject.key,
        });

        setEncryptedText(encryptedResult.encryptedText);

        if (state.isLoggedIn) {
          result = await keyService.upload({
            encryptionKey: encryptionKeyObject.key,
            subject: finalSubject,
            name: `Key for ${finalSubject}`
          });

          setMessageId(result.message.id);
          setKeyId(result.key.id);
          result.encryptedText = encryptedResult.encryptedText;
          result.subject = finalSubject;
        } else {
          const newKey = await keyService.createKey({
            encryptionKey: encryptionKeyObject.key,
            name: `Key for ${finalSubject}`,
            subject: finalSubject
          });

          result = {
            message: null,
            key: newKey,
            isNewKey: true,
            isLocalKey: true,
            encryptedText: encryptedResult.encryptedText,
            subject: finalSubject
          };
          setKeyId(newKey.id);
        }
      }

      return result;
    } catch (error) {
      console.error("Error during encryption:", error);
      throw error;
    }
  };

  return (
    <NewTextbox
      handleTextChange={handleChange}
      handleSubjectChange={handleSubjectChange}
      applyTemplate={applyTemplate}
      encryptedText={encryptedText}
      text={textToEncryption}
      showKey={showKey}
      isLoggedIn={state.isLoggedIn}
      subject={subject}
      createdDate={createdDate}
      encryptionKey={encryptionKeyObject.key}
      messageId={messageId}
      keyId={keyId}
      onGenerateEncryption={handleGenerateEncryption}
    />
  );
};

export default NewTextboxContainer;
